/**
 * Aurora theme — fragment shader.
 *
 * Adapted from the shadertoy "Lights in Smoke" source
 * (https://www.shadertoy.com/view/MdyGzR). Three colored point
 * lights on parametric paths, each modulated by a six-octave
 * simplex-noise fbm (`clouds(uv)`) so the lights look like they're
 * filtering through animated cloud volumes — northern-lights vibe.
 *
 * Adaptations vs the shadertoy source (verbatim helpers, mutated
 * `main`):
 *
 *   1. `mainImage(out vec4 fragColor, in vec2 fragCoord)` → `void
 *      main()` reading `gl_FragCoord.xy` directly. Required for
 *      `#version 300 es` (the WebGL2 fragment-shader entry point
 *      writes to a declared `out vec4 fragColor` global, not a
 *      mainImage parameter).
 *
 *   2. The three `vec3 lightColorN` local literals are replaced by
 *      `uniform vec3 uLightColorN` reads so the dispatcher can
 *      inject per-variant palettes (emerald/magenta/teal for
 *      `aurora-dark`, pastel mint/pink/sky for `aurora-light`)
 *      without recompiling the shader.
 *
 *   3. New `uniform vec3 uBgBase` + `uniform float uBgIntensityMul`
 *      compose the final output as `uBgBase + lights * uBgMul`.
 *      Dark variant: near-black bg, full-strength lights. Light
 *      variant: warm-cream bg, softened lights (0.55 mul) so
 *      pastel aurora reads against pale chrome without
 *      overwhelming it.
 *
 *   4. `uKwhScale` (0-1) drives two visible-growth effects per
 *      light (range widened post-runtime-review — the original
 *      0.3→1.1 / 100→40 range read as a snap in the picker because
 *      the start state was already bright):
 *        - `cloudIntensityN *= (0.05 + uKwhScale * 1.05)` —
 *          session-start lights are near-invisible (0.05×),
 *          session-complete lights are vivid (1.1× overdrive).
 *        - falloff divisor changes from 150.0 → 50.0 as
 *          uKwhScale grows 0 → 1, broadening each light's halo
 *          spread (very tight points at session start, wide
 *          luminous regions at session end).
 *
 * Cache key: 'aurora:dark' or 'aurora:light' (the variant suffix
 * distinguishes the compiled programs even though source is
 * identical post-AURORA_VARIANT-injection — the per-variant
 * uniform values live in the dispatcher, not the source). The
 * AURORA_VARIANT `#define` is injected by `prependAuroraVariant`
 * below for forward-compat with future variant-branching inside
 * the shader source; not currently consumed by any `#if`
 * directive in the body.
 *
 * Helpers (snoise, mod289 × 2, permute, taylorInvSqrt, normnoise,
 * clouds) are verbatim from the shadertoy source — they're the
 * standard Ashima Arts simplex-noise port (Stefan Gustavson + Ian
 * McEwan) + a normalized-noise convenience + the six-octave fbm
 * the original demo uses. No project conflicts (confirmed via
 * grep across the charging/ folder pre-transcription).
 *
 * `uIntensity` (powerKw-driven) intentionally not declared. The
 * dispatcher's per-frame bind passes it for every shader, but
 * `bindUniforms` silently skips uniforms whose getUniformLocation
 * returns null — so the Aurora shader cleanly opts out without a
 * declaration. Future hookup (e.g. brightening lights with
 * instantaneous power) can declare it then.
 */
export const AURORA_SHADER_SOURCE = `#version 300 es
precision highp float;
#define AURORA_DARK  0
#define AURORA_LIGHT 1
// AURORA_VARIANT injected via prepend:
// #define AURORA_VARIANT AURORA_DARK
uniform float iTime;
uniform vec2 iResolution;
uniform float uKwhScale;
// Tap-to-scatter interaction (Aurora). 0 at rest (identity), so the lights sit
// in their baseline pattern; a tap pushes them outward, then they recollect.
uniform float uScatter;
uniform vec3 uLightColor1;
uniform vec3 uLightColor2;
uniform vec3 uLightColor3;
uniform vec3 uBgBase;
uniform float uBgIntensityMul;
out vec4 fragColor;

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
     return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v)
  {
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

// First corner
  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 =   v - i + dot(i, C.xxx) ;

// Other corners
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

// Permutations
  i = mod289(i);
  vec4 p = permute( permute( permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

  float n_ = 0.142857142857;
  vec3  ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_ );

  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),
                                dot(p2,x2), dot(p3,x3) ) );
  }

float normnoise(float noise) {
  return 0.5*(noise+1.0);
}

float clouds(vec2 uv) {
    uv += vec2(iTime*0.05, + iTime*0.01);

    vec2 off1 = vec2(50.0,33.0);
    vec2 off2 = vec2(0.0, 0.0);
    vec2 off3 = vec2(-300.0, 50.0);
    vec2 off4 = vec2(-100.0, 200.0);
    vec2 off5 = vec2(400.0, -200.0);
    vec2 off6 = vec2(100.0, -1000.0);
    float scale1 = 3.0;
    float scale2 = 6.0;
    float scale3 = 12.0;
    float scale4 = 24.0;
    float scale5 = 48.0;
    float scale6 = 96.0;
    return normnoise(snoise(vec3((uv+off1)*scale1,iTime*0.5))*0.8 +
                     snoise(vec3((uv+off2)*scale2,iTime*0.4))*0.4 +
                     snoise(vec3((uv+off3)*scale3,iTime*0.1))*0.2 +
                     snoise(vec3((uv+off4)*scale4,iTime*0.7))*0.1 +
                     snoise(vec3((uv+off5)*scale5,iTime*0.2))*0.05 +
                     snoise(vec3((uv+off6)*scale6,iTime*0.3))*0.025);
}

void main() {
  vec2 fragCoord = gl_FragCoord.xy;
  vec2 uv = fragCoord.xy / iResolution.x;

  vec2 center = vec2(0.5, 0.5*(iResolution.y/iResolution.x));

  // Tap-to-scatter: expand the sample outward from centre by the impulse, so
  // the lights spread out, then recollect as uScatter decays to 0 (identity).
  uv = center + (uv - center) / (1.0 + uScatter * 2.0);

  // Three light positions on parametric paths — verbatim from
  // the shadertoy source. Color values moved to uniforms below.
  vec2 light1 = vec2(sin(iTime*1.2+50.0)*1.0 + cos(iTime*0.4+10.0)*0.6,
                     sin(iTime*1.2+100.0)*0.8 + cos(iTime*0.2+20.0)*-0.2) * 0.2 + center;
  vec2 light2 = vec2(sin(iTime+3.0)*-2.0, cos(iTime+7.0)*1.0) * 0.2 + center;
  vec2 light3 = vec2(sin(iTime+3.0)*2.0,  cos(iTime+14.0)*-1.0) * 0.2 + center;

  // uKwhScale modulations (widened range — see commit; the prior
  // 0.3→1.1 cloud / 100→40 falloff range read as a snap because
  // the start state was already bright and the dominant point-
  // light term barely changed):
  //   - Cloud-intensity multiplier ramps from 0.05 (session start,
  //     near-invisible clouds) to 0.89 at the 80% picker preview,
  //     1.10 at session complete (slight overdrive, capped by the
  //     final blend). Dormant→dominant rather than dim→bright.
  //   - Falloff divisor reduces from 150.0 (very tight, small
  //     point lights at session start) to 70.0 at the 80% preview,
  //     50.0 at session complete — ~2x wider halo spread at the
  //     preview state than the old range produced.
  float kwhCloudMul = 0.05 + uKwhScale * 1.05;
  float kwhFalloffDiv = 150.0 - uKwhScale * 100.0;

  float cloudIntensity1 = 0.7 * (1.0 - (2.5 * distance(uv, light1))) * kwhCloudMul;
  float lighIntensity1  = 1.0 / (kwhFalloffDiv * distance(uv, light1));

  float cloudIntensity2 = 0.7 * (1.0 - (2.5 * distance(uv, light2))) * kwhCloudMul;
  float lighIntensity2  = 1.0 / (kwhFalloffDiv * distance(uv, light2));

  float cloudIntensity3 = 0.7 * (1.0 - (2.5 * distance(uv, light3))) * kwhCloudMul;
  float lighIntensity3  = 1.0 / (kwhFalloffDiv * distance(uv, light3));

  // Final composition — bg sits as the base layer, light
  // contributions add on top, intensity multiplier controls how
  // dominant the lights feel against the bg. Dark variant
  // (uBgBase ~near-black, uBgIntensityMul = 1.0) lets the lights
  // dominate. Light variant (uBgBase ~warm-cream,
  // uBgIntensityMul = 0.55) softens the composition so pastel
  // aurora reads against pale chrome without overwhelming it.
  vec3 auroraColor = uBgBase + (
    vec3(cloudIntensity1 * clouds(uv)) * uLightColor1 + lighIntensity1 * uLightColor1 +
    vec3(cloudIntensity2 * clouds(uv)) * uLightColor2 + lighIntensity2 * uLightColor2 +
    vec3(cloudIntensity3 * clouds(uv)) * uLightColor3 + lighIntensity3 * uLightColor3
  ) * uBgIntensityMul;

  // Canonical two-term wind-up, applied once at the composition
  // point so every layer (lights, clouds, halo) gets the same
  // treatment. Composed in this order: desaturate first, then
  // darken.
  //
  //   1. kwhSaturation (grayscale -> full colour). Linear in
  //      uKwhScale. Mix between the Rec.709 luminance of the
  //      output and the full-colour output, so the start state is
  //      colourless form and the end state is full hue. Rec.709
  //      weights (0.2126/0.7152/0.0722) are the sRGB standard the
  //      shaders operate in (not Rec.601). Linear curve for now;
  //      tune to pow()/smoothstep() in a follow-up only if the
  //      live walk wants it.
  //   2. kwhBrightness (near-black -> full). Unchanged: 0.05 at
  //      session start, 0.81 at the 80% picker preview, 1.0 at
  //      session complete. Multiplies AFTER desaturation so it
  //      darkens whichever (gray or colour) content step 1
  //      produced.
  //
  // Net: colourless + dim at session start, vibrant + bright at
  // full charge — the wind-up reads as "coming to life."
  float luminance = dot(auroraColor, vec3(0.2126, 0.7152, 0.0722));
  float kwhSaturation = uKwhScale;
  vec3 windupColor = mix(vec3(luminance), auroraColor, kwhSaturation);
  float kwhBrightness = 0.05 + uKwhScale * 0.95;
  fragColor = vec4(windupColor * kwhBrightness, 1.0);
}`;

/**
 * Aurora shader variant names. Two values matching the
 * `Theme.shaderVariant` axis on the Aurora family registry entry
 * and the per-variant uniform tuples in `AURORA_UNIFORMS` below.
 */
export type AuroraVariant = 'aurora-dark' | 'aurora-light';

/**
 * Numeric values for the `#define AURORA_VARIANT` injection.
 * Match the literal `#define AURORA_DARK 0 / AURORA_LIGHT 1`
 * declarations at the top of the GLSL string above. Currently no
 * `#if AURORA_VARIANT == ...` branches consume the value (palette
 * is uniform-injected, not source-branched), but the macro is
 * declared + injected for forward-compat — a future variant-
 * specific motion tweak or per-variant light-count change can
 * gate on this without restructuring the dispatcher.
 */
export const AURORA_VARIANT_CONSTANTS: Record<AuroraVariant, number> = {
  'aurora-dark': 0,
  'aurora-light': 1,
};

/**
 * Per-variant uniform tuples. Tuples are arrays so they can be
 * passed to `gl.uniform3fv` / passed to `bindUniforms` directly
 * (which dispatches on tuple length). All vec3 values in
 * linear-RGB [0, 1] floats. `uBgIntensityMul` scales the summed
 * light contribution against the bg base.
 *
 * Dark variant palette: emerald + magenta + deep teal on
 * near-black with slight blue lean. Lights at full strength
 * (uBgIntensityMul = 1.0) because the dark bg gives them
 * maximum contrast.
 *
 * Light variant palette: pastel mint + pastel pink + pastel sky
 * on warm cream. Lights at 0.55× because pastel-on-cream washes
 * out at full strength — the lower multiplier keeps the aurora
 * legible without overwhelming the pale surface.
 */
export const AURORA_UNIFORMS = {
  'aurora-dark': {
    uLightColor1: [0.10, 0.95, 0.45],
    uLightColor2: [0.92, 0.20, 0.70],
    uLightColor3: [0.10, 0.55, 0.75],
    uBgBase: [0.00, 0.00, 0.05],
    uBgIntensityMul: 1.0,
  },
  'aurora-light': {
    uLightColor1: [0.55, 0.85, 0.70],
    uLightColor2: [0.92, 0.70, 0.85],
    uLightColor3: [0.65, 0.80, 0.90],
    uBgBase: [0.96, 0.94, 0.92],
    uBgIntensityMul: 0.55,
  },
} as const satisfies Record<AuroraVariant, {
  uLightColor1: readonly [number, number, number];
  uLightColor2: readonly [number, number, number];
  uLightColor3: readonly [number, number, number];
  uBgBase: readonly [number, number, number];
  uBgIntensityMul: number;
}>;

/**
 * Returns the Aurora shader source with `#define AURORA_VARIANT
 * <number>` injected immediately after the `#version 300 es`
 * directive. GLSL requires `#version` to be the first directive
 * in the source; the inject lands at line 2 so it's in scope for
 * any future `#if AURORA_VARIANT == ...` branches in the body.
 *
 * Called by the WebGL compile pipeline in
 * ShaderChargingAnimation.tsx to produce one compiled program per
 * aurora variant. Each variant compiles to its own GL program
 * object even though the source post-injection is currently
 * identical between dark and light (the AURORA_VARIANT macro is
 * unused in the body today; declared for forward-compat). The
 * cache key — `'aurora:dark'` / `'aurora:light'` — keeps the two
 * programs distinct in the per-context cache so a future
 * source-branching addition doesn't require dispatcher changes.
 *
 * Mirrors the prependOrbKind helper shape from orbComposite.ts.
 */
export function prependAuroraVariant(variant: AuroraVariant): string {
  const kind = AURORA_VARIANT_CONSTANTS[variant];
  const lines = AURORA_SHADER_SOURCE.split('\n');
  const versionIdx = lines.findIndex((line) => line.startsWith('#version'));
  if (versionIdx === -1) {
    // Defensive — should never trip since the source above starts
    // with `#version 300 es`. Returning the raw source here would
    // produce a compile error downstream (no AURORA_VARIANT
    // defined), which is the right failure surface.
    return AURORA_SHADER_SOURCE;
  }
  const before = lines.slice(0, versionIdx + 1);
  const after = lines.slice(versionIdx + 1);
  const inject = `#define AURORA_VARIANT ${kind}`;
  return [...before, inject, ...after].join('\n');
}
